/**
 * Social Auth SDK Utilities
 * Handles loading Google Identity Services and Apple JS SDKs
 */

// --------------- Shared ---------------

/**
 * Reads the claims out of an id token WITHOUT verifying it. Only ever used to PRE-FILL a form
 * (name / email the person can still edit) — never as proof of anything. The backend is what
 * verifies a token, and the email still goes through its own code check.
 */
export function decodeJwt(token) {
  try {
    const part = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(part).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// --------------- Google ---------------

let googleSDKLoaded = false;

export function loadGoogleSDK() {
  if (googleSDKLoaded && window.google?.accounts) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    if (document.getElementById('google-identity-script')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-identity-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      googleSDKLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

/**
 * Same two-stage flow as the plugin's socialAuth.js: try the One Tap account
 * chooser FIRST — the in-page overlay at the top-right that lists the browser's
 * signed-in Google accounts — and only fall back to the popup window (hidden GIS
 * button, programmatic click) when One Tap cannot display: incognito, no Google
 * session, cooldown, or third-party-cookie constraints.
 */
export function triggerGoogleSignIn(clientId) {
  return new Promise((resolve, reject) => {
    if (!window.google?.accounts) {
      reject(new Error('Google SDK not loaded'));
      return;
    }

    let settled = false;
    const safeResolve = (value) => { if (!settled) { settled = true; clearTimeout(safetyTimer); resolve(value); } };
    const safeReject = (err) => { if (!settled) { settled = true; clearTimeout(safetyTimer); reject(err); } };

    // Covers both stages: give up quietly if nothing happens within 2 minutes.
    const safetyTimer = setTimeout(() => {
      safeReject(new Error('Sign-in cancelled'));
    }, 120000);

    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response.credential) {
          safeResolve(response.credential);
        } else {
          safeReject(new Error('Google sign-in failed'));
        }
      },
    });

    // Stage 2 — popup window via a hidden GIS button.
    const openPopupFallback = () => {
      if (settled) return;

      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '-9999px';
      document.body.appendChild(container);

      window.google.accounts.id.renderButton(container, {
        type: 'icon',
        size: 'large',
      });

      setTimeout(() => {
        const btn = container.querySelector('[role="button"]') || container.querySelector('div[id^="g_"]');
        if (!btn) {
          if (container.parentNode) document.body.removeChild(container);
          safeReject(new Error('popup_blocked'));
          return;
        }

        const origOpen = window.open;
        let popupRef = null;
        window.open = function (...args) {
          const popup = origOpen.apply(this, args);
          window.open = origOpen;
          if (!popup || popup.closed) {
            safeReject(new Error('popup_blocked'));
          } else {
            popupRef = popup;
          }
          return popup;
        };

        btn.click();

        setTimeout(() => {
          window.open = origOpen;
        }, 1000);

        const pollTimer = setInterval(() => {
          if (settled) {
            clearInterval(pollTimer);
            if (container.parentNode) document.body.removeChild(container);
            return;
          }
          try {
            if (popupRef && popupRef.closed) {
              clearInterval(pollTimer);
              if (container.parentNode) document.body.removeChild(container);
              safeReject(new Error('Sign-in cancelled'));
            }
          } catch (_) {}
        }, 500);
      }, 300);
    };

    // Stage 1 — One Tap.
    window.google.accounts.id.prompt((notification) => {
      if (notification.isDismissedMoment()) {
        // 'credential_returned' is the SUCCESS dismissal — the account was picked
        // and initialize()'s callback resolves; rejecting here would race it.
        if (notification.getDismissedReason?.() !== 'credential_returned') {
          safeReject(new Error('Sign-in cancelled'));
        }
        return;
      }

      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        openPopupFallback();
      }
    });
  });
}

// --------------- Apple ---------------

let appleSDKLoaded = false;

export function loadAppleSDK() {
  if (appleSDKLoaded && window.AppleID) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    if (document.getElementById('apple-signin-script')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = 'apple-signin-script';
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      appleSDKLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Apple Sign-In SDK'));
    document.head.appendChild(script);
  });
}

export function triggerAppleSignIn(config) {
  return new Promise(async (resolve, reject) => {
    if (!window.AppleID) {
      reject(new Error('Apple SDK not loaded'));
      return;
    }

    try {
      window.AppleID.auth.init({
        clientId: config.clientId,
        scope: config.scope || 'name email',
        redirectURI: config.redirectURI,
        usePopup: true,
      });

      const response = await window.AppleID.auth.signIn();

      if (response.authorization) {
        resolve({
          id_token: response.authorization.id_token,
          // ⚠️ Apple's identity token carries NO name claims, and `response.user`
          // is populated ONLY on the very first authorization for this Apple ID —
          // never on any later sign-in, and never again even if the app is
          // uninstalled and re-authorized (short of the user revoking access in
          // their Apple ID settings). If these aren't forwarded to the backend on
          // that one occasion, the account is created nameless permanently.
          firstName: response.user?.name?.firstName || '',
          lastName: response.user?.name?.lastName || '',
        });
      } else {
        reject(new Error('Apple sign-in failed'));
      }
    } catch (err) {
      if (err.error === 'popup_closed_by_user') {
        reject(new Error('Sign-in cancelled'));
      } else {
        reject(new Error(err.error || 'Apple sign-in failed'));
      }
    }
  });
}

// --------------- Config ---------------

/**
 * ⚠️ THE BACKEND IS THE ONLY SOURCE. `/config/app` returns a `social_auth` block with a
 * client id per platform, and this portal uses exactly one: **`google_web_client_id`** —
 * passenger, web. (The driver portal uses `google_driver_web_client_id`; they are
 * separate applications and must never share a client.)
 *
 * These were hardcoded until 2026-08-25: a client id copied by hand, plus two literal
 * placeholders (`YOUR_APPLE_SERVICE_ID`, `Apple_Redirect_URI`) that could only ever have
 * failed. A hardcoded credential silently outranks the one the backend serves, so
 * rotating it there fixes nothing — which is exactly how the driver portal ended up
 * showing `origin_mismatch` against a client from a different Google project.
 *
 * ⚠️ TAKES THE CONFIG AS AN ARGUMENT — it does NOT reach into the react-query cache
 * itself. A version that did kept returning an empty id in the driver portal even with
 * `/config/app` answering 200, and that failure is unfalsifiable from the outside: a
 * wrong key, a duplicated module, a second QueryClient and a plain timing miss all look
 * identical. Passing the value the caller's `useAppConfig()` already has makes this a
 * pure function of its input.
 *
 * ⚠️ An empty id must disable the button, not be papered over with a default. See
 * SocialLoginButtons — this applies to Apple exactly as it does to Google.
 *
 * @param {object|null} appConfig  the payload from `useAppConfig()` — `/config/app`'s data
 */
export function getSocialAuthConfig(appConfig) {
  const social = appConfig?.social_auth || {};

  return {
    googleClientId: social.google_web_client_id || '',
    // Mirrors the Google line: the *web* id is the one this dashboard wants.
    // `apple_web_client_id` must be an Apple **Services ID** — a separate identifier from
    // the iOS bundle id in `apple_ios_client_id`. If the backend ever serves the bundle id
    // here (the two were briefly the same string), Apple answers `invalid_client`.
    // No fallback to `apple_client_id` — social-signin-web.md names `apple_web_client_id`
    // as the web portal's only Apple id. Empty → `appleReady` is false and the button
    // stays off, instead of opening Apple with an id it rejects (`invalid_client`).
    appleServiceId: social.apple_web_client_id || '',
    // NOT a credential and NOT something the backend sends — it is simply where Apple
    // returns to, which is wherever this app is served from. Unlike the ids above, a local
    // value is the correct answer here. (This read `''` while Apple was behind a
    // coming-soon flag; an empty redirectURI is rejected outright by the Apple SDK.)
    //
    // ⚠️ Apple matches this against the Services ID's registered Return URLs *exactly*,
    // so every origin this app is served from — live and staging — has to be listed
    // there, and the domain verified. An unregistered origin fails with `invalid_client`,
    // the same error a wrong Services ID gives.
    //
    // `/apple-return` is NOT a real route and nothing needs to serve it: the popup
    // redirects there internally and is read by the SDK, so the page never navigates.
    // It exists only to give Apple a registrable string. Changing it means re-registering
    // in the Apple console first, or every sign-in breaks.
    appleRedirectURI: `${window.location.origin}/apple-return`,
  };
}
